// Every number and vocabulary the achievement authoring spec names
// (docs/superpowers/specs/2026-10-10-achievement-authoring-design.md), in one
// pure module shared by the client, the server, the Functions and the tests.
// MiB and KB values are binary (1 MiB = 1,048,576 bytes; the spec's 40 KB payload
// is its own CHECK's 40,960 bytes, so 32 KB and 64 KB are 32,768 and 65,536);
// the spec's MB and GB values are decimal (8 MB = 8,000,000 bytes).
import { ACHIEVEMENT_SOURCES } from './achievement-source.js';

function deepFreeze(value) {
  if (value !== null && typeof value === 'object' && !Object.isFrozen(value)) {
    Object.freeze(value);
    for (const child of Object.values(value)) deepFreeze(child);
  }
  return value;
}

/** Systems author mode supports (spec 8.1). n64 is out of version 1. */
export const AUTHORING_SYSTEMS = deepFreeze(['nes', 'snes', 'gb', 'gbc', 'gba', 'genesis', 'atari2600', 'c64']);

/** Per-item, per-game, payload and listing limits (spec 6.3, 6.5, 7.2, 9.3). */
export const LIMITS = deepFreeze({
  marksActivePerItem: 40,
  marksTotalPerItem: 200,
  versionsPerItem: 200,
  checksPerItem: 500,
  testRunsPerVersion: 100,
  testFramesPerVersion: 20,
  itemsPerGame: 500,
  displaySeqMax: 500,
  labelsPerGameHash: 2000,
  scopePairsPerVersion: 100,
  payloadBytesMax: 40960,
  triggerBytesMax: 32768,
  conditionsMax: 64,
  depthMax: 4,
  hitTargetMax: 3600,
  nameMax: 64,
  descriptionMax: 200,
  labelMax: 40,
  noteMax: 200,
  versionsPerDefinitionsRequest: 100,
  draftListPageSize: 100,
});

/** Evidence quotas and the fence cap (spec 6.5, 7.12). */
export const QUOTA = deepFreeze({
  perAdminBytes: 2_000_000_000,
  globalBytes: 20_000_000_000,
  fenceCountMax: 1_000_000,
  fenceAlertFraction: 0.8,
});

/** Stored and decoded caps per evidence file kind (spec 6.5). */
export const FILE_CAPS = deepFreeze({
  memory: { storedBytes: 8_000_000, decodedBytes: 67_108_864, samplesMax: 1024 },
  frames: { storedBytes: 4_000_000, framesMax: 1024, maxWidth: 160, maxHeight: 240 },
  test_frames: { storedBytes: 4_000_000, framesMax: 1024, maxWidth: 160, maxHeight: 240 },
  checkpoint_near: { storedBytes: 8_000_000, decodedBytes: 16_777_216 },
  checkpoint_early: { storedBytes: 8_000_000, decodedBytes: 16_777_216 },
});

/**
 * Authored cache and delivery bounds (spec 7.1, 7.7). deliveryPairBytesMax
 * bounds the complete response of one (rom_hash, core_name) delivery (spec 7.1:
 * the route copies the pair's precomputed bytes, at most 2 MiB).
 * deliveryEnvelopeBytesMax bounds everything in that response that is not an
 * entry (braces, keys, generation, asOf, the game object, the null score
 * placeholders), and deliveryEntriesBytesMax, the rest, bounds the sum of the
 * pair's entries (their payload_bytes): publish and re-parent admission check
 * it, and the build suppresses a pair over the entries or response bound; the
 * envelope is bounded by construction, since every delivered game's metadata is
 * at most GAME_METADATA_BYTES_MAX (Task 3, the delivery size rule). The envelope
 * bound is this plan's number; the spec leaves it open. snapshotBytesMax (spec
 * 7.7: 24 MiB) bounds the whole retained snapshot, so the versions' summed
 * authoredCacheCost may use only snapshotVersionBytesMax, what is left after the
 * fixed snapshot head reserve (two per-entry costs: the body head and the
 * empty-delivery record). The builder (Task 10), the re-parenting function
 * (Task 5's v_snapshot_max) and publish admission (Task 17) all compare with it.
 */
export const CACHE = deepFreeze({
  snapshotBytesMax: 25_165_824,
  snapshotHeadReserveBytes: 2 * 128,
  snapshotVersionBytesMax: 25_165_824 - 2 * 128,
  aggregateBytesMax: 67_108_864,
  deliveryPairBytesMax: 2_097_152,
  deliveryEnvelopeBytesMax: 65_536,
  deliveryEntriesBytesMax: 2_097_152 - 65_536,
  pageVersions: 100,
  perEntryCost: 128,
  scoreParsedMultiplier: 4,
  staleSnapshotMs: 120_000,
  refreshIntervalMs: 60_000,
  buildDeadlineMs: 30_000,
  startupLoadMs: 60_000,
  deliveryMaxAgeSeconds: 30,
  deliveryResponseAccountBytes: 33_554_432,
  deliveryConcurrentMax: 64,
});

/** Request deadlines, admission gates and sweeper bounds (spec 7.1, 7.5, 7.12). */
export const SERVER = deepFreeze({
  readDeadlineMs: 10_000,
  writeDeadlineMs: 30_000,
  postCommitDeadlineMs: 10_000,
  requestConcurrentMax: 16,
  requestConcurrentPerAdmin: 4,
  requestAccountBytes: 50_331_648,
  jsonBodyBytes: 65_536,
  uploadConcurrentMax: 2,
  uploadConcurrentPerAdmin: 1,
  uploadLeaseMs: 90_000,
  downloadConcurrentMax: 4,
  downloadConcurrentPerAdmin: 1,
  downloadAccountBytes: 33_554_432,
  sweepPageRows: 100,
  sweepBatchDeadlineMs: 60_000,
  declaredExpiryMs: 1_209_600_000,
  lockTimeoutMs: 5_000,
  statementTimeoutMs: 30_000,
});

/** Recording, marking, checkpoint and overlay timings in ms (spec 7.7, 8.3 to 8.6, 9.1, 10.2). */
export const TIMINGS = deepFreeze({
  toleranceMs: 500,
  preEventTargetMs: 10_000,
  preEventMinMs: 3_000,
  postEventMinMs: 250,
  historyMs: 15_000,
  checkpointIntervalMs: 5_000,
  checkpointNearGapMs: 2_000,
  checkpointDeadlineMs: 2_000,
  checkpointNeverSettledMs: 10_000,
  overlayValidityMs: 600_000,
  overlayRefetchMs: 300_000,
  loadDeadlineMs: 15_000,
  fetchDeadlineMs: 10_000,
  duplicateMarkMs: 500,
});

/** Client overlay bounds (spec 9.1, 9.3). */
export const CLIENT = deepFreeze({
  publishedOverlayBytesMax: 2_097_152,
  draftActiveBytesMax: 4_194_304,
  overlayAccountBytes: 67_108_864,
  parsedMultiplier: 4,
});

/** Publish rules (spec 6.3 points and score checks, 7.5 display order). */
export const PUBLISH = deepFreeze({
  pointsMin: 1,
  pointsMax: 100,
  displayOrderBase: 1_000_000,
  scoreMax: 9_007_199_254_740_991,
});

/**
 * Score definition rules (spec 7.2). termMulMax bounds a sum-of-terms term's
 * `mul`, which the spec leaves open; authoring-schema.js also requires the
 * largest decodable total to stay a safe integer.
 */
export const SCORE_RULES = deepFreeze({
  lenMin: 1,
  lenMax: 4,
  termsMax: 4,
  multipliers: [1, 10, 100],
  termMulMax: 100_000_000,
});

/** Evidence minimums (spec 7.4 item 2, 10.2 item 1). */
export const EVIDENCE_MINIMUMS = deepFreeze({
  positiveEvents: 2,
  distinctScoreReadings: 2,
  gameStarts: 2,
  gameOvers: 2,
  gameCancels: 2,
});

/**
 * Delivery 503 retries in the client loader (spec 9.3), and the most bytes the
 * loader reads from one delivery response before parsing (spec 9.1: the client
 * refuses a response larger than its bound, read with a byte limit): the
 * complete pair response bound, 2 MiB.
 */
export const DELIVERY_CLIENT = deepFreeze({
  unavailableRetries: 3,
  unavailableRetryWindowMs: 10_000,
  responseBytesMax: CACHE.deliveryPairBytesMax,
});

/** Author-mode (studio, PR 2) budgets and policies (spec 8.4 to 8.9, 10). */
export const STUDIO = deepFreeze({
  sessionBudgetBytes: 100_663_296,
  memoryArenaBytes: 33_554_432,
  frameRingBytes: 16_777_216,
  checkpointBudgetBytes: 41_943_040,
  checkpointRingMax: 12,
  manualCheckpointsMax: 4,
  checkpointNullStreakMax: 2,
  keyframeIntervalMs: 1_000,
  keyframeDiffFraction: 0.25,
  historyEvictStepMs: 1_000,
  preEventTrimStepMs: 1_000,
  frameCaptureWidth: 160,
  pressureCheckpoints: 3,
  pressureHistoryMs: 5_000,
  inferenceBudgetMs: 10_000,
  inferenceCandidatesMax: 10_000,
  guardsMax: 2,
  sumSearchBytes: 16,
  stagedEvidenceBytesMax: 200_000_000,
  stagedExpiryMs: 1_209_600_000,
  uploadBackoffMaxMs: 300_000,
});

// Vocabularies (spec 6.3, 7.3, 7.4, 7.5, 7.9, 8.2, 10.1, 10.5).
export const ITEM_KINDS = deepFreeze(['achievement', 'score']);
export const ITEM_STATUSES = deepFreeze(['draft', 'published', 'unpublished']);
export const EVENT_TYPES = deepFreeze([
  'life_lost', 'level_reached', 'level_complete', 'item_collected', 'boss_defeated', 'game_over', 'custom', 'score',
]);
export const MARK_ROLES = deepFreeze(['event', 'score_reading', 'game_start', 'game_over', 'game_cancel']);
export const POLARITIES = deepFreeze(['positive', 'negative']);
export const FILE_KINDS = deepFreeze(['memory', 'frames', 'checkpoint_near', 'checkpoint_early', 'test_frames']);
export const FILE_STATES = deepFreeze(['absent', 'declared', 'attached', 'tombstoned']);
export const TEST_OUTCOMES = deepFreeze(['fired', 'missed', 'score']);
export const VERDICTS = deepFreeze(['pending', 'confirmed', 'false_positive', 'rejected']);
export const START_PROVENANCES = deepFreeze(['fresh_boot', 'checkpoint']);
export const AUDIT_ACTIONS = deepFreeze([
  'create', 'new_version', 'restore', 'check', 'test', 'verdict', 'mark_abandoned', 'publish', 'unpublish',
  'republish', 'delete', 'leaderboard_cleared', 'superseded_by_static', 'reparented',
]);
export const CHECK_REASONS = deepFreeze([
  'fired_early', 'not_fired', 'fired_on_negative', 'fired_in_quiet', 'score_mismatch', 'no_zero_checkpoint',
  'not_evaluated', 'too_few_marks', 'conflicting_marks',
]);
export const ACTOR_KINDS = deepFreeze(['user', 'system']);
export const QUOTA_ACCOUNT_KINDS = deepFreeze(['global', 'user']);
export const VERDICT_INPUTS = deepFreeze(['confirmed', 'false_positive', 'rejected']);
export const LEADERBOARD_CHOICES = deepFreeze(['keep', 'clear']);
export const READY_FAILURES = deepFreeze(['minimums', 'evidence', 'check', 'false_positive', 'missed', 'scope']);
export const REFRESH_FAILURE_CODES = deepFreeze(['timeout', 'capacity', 'db_error', 'storage_error', 'notify_failed']);
export const SCORE_REJECTIONS = deepFreeze(['stale_version', 'stale_epoch', 'out_of_scope', 'not_supported']);
export const AUTHORED_SYNC_SKIP_REASONS = deepFreeze(['not_published', 'out_of_scope']);
export const FRAME_SOURCES = deepFreeze(['emulated', 'raf', 'raf-virtual']);

/** Blob-key extension per file kind (spec 6.5: authoring/<item_id>/f/<file_id>.<ext>). */
export const FILE_KIND_EXTENSIONS = deepFreeze({
  memory: 'mem',
  frames: 'frames',
  test_frames: 'frames',
  checkpoint_near: 'state',
  checkpoint_early: 'state',
});

/**
 * The fixed achievement icon list (spec 7.2), built from code points. Append only: never remove or
 * reorder an icon, since stored versions keep theirs, ICONS[0] is the default, and a cached client
 * shows the default for an icon it does not know yet (authored-loader.js).
 */
export const ICONS = deepFreeze([
  0x1f3c6, // trophy
  0x1f947, // first place medal
  0x1f948, // second place medal
  0x1f949, // third place medal
  0x2b50, // star
  0x1f31f, // glowing star
  0x1f3af, // direct hit
  0x1f3ae, // video game
  0x1f451, // crown
  0x1f48e, // gem stone
  0x1f525, // fire
  0x26a1, // high voltage
  0x1f480, // skull
  0x1f511, // key
  0x1f680, // rocket
  0x1f3c1, // chequered flag
].map((codePoint) => String.fromCodePoint(codePoint)));

/** Bumped whenever the engine's evaluation changes (spec 10.5). */
export const ENGINE_CHECK_VERSION = 'engine-check-1';

/**
 * Shared format guards (spec 7.1). ECMAScript expressions without the `m`
 * flag, anchored, so `$` matches only at the end of the input. The container
 * is frozen; the expressions are not (a frozen RegExp cannot track lastIndex).
 */
export const FORMAT_RE = Object.freeze({
  id: /^[1-9][0-9]{0,17}$/,
  romHash: /^[0-9a-f]{8}$/,
  coreName: /^[a-z0-9_]{1,64}$/,
  uuid: /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/,
  sha256Hex: /^[0-9a-f]{64}$/,
});

/** Every SQL CHECK vocabulary (spec 6.2, 6.3); Task 5's migration test compares them. */
export const SQL_CHECK_VOCABULARIES = deepFreeze({
  'achievements.source': ACHIEVEMENT_SOURCES,
  'games.score_source': ACHIEVEMENT_SOURCES,
  'authored_items.kind': ITEM_KINDS,
  'authored_items.status': ITEM_STATUSES,
  'authored_item_versions.event_type': EVENT_TYPES,
  'authored_marks.event_type': EVENT_TYPES,
  'authored_marks.role': MARK_ROLES,
  'authored_marks.polarity': POLARITIES,
  'authored_files.kind': FILE_KINDS,
  'authored_files.state': FILE_STATES,
  'authoring_quota_accounts.kind': QUOTA_ACCOUNT_KINDS,
  'authored_test_runs.start_provenance': START_PROVENANCES,
  'authored_test_runs.outcome': TEST_OUTCOMES,
  'authored_test_runs.verdict': VERDICTS,
  'authored_audit.action': AUDIT_ACTIONS,
  'authored_audit.actor_kind': ACTOR_KINDS,
  'play_sessions.score_rejected': SCORE_REJECTIONS,
});
